import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFetcher } from 'react-router'
import { Icon } from '#app/components/ui/icon.tsx'
import {
	Popover,
	PopoverClose,
	PopoverContent,
	PopoverTrigger,
} from '#app/components/ui/popover.tsx'
import { Segmented } from '#app/components/ui/segmented.tsx'
import { Slider } from '#app/components/ui/slider.tsx'
import { useOptimisticThemeMode } from '#app/routes/resources/theme-switch.tsx'
import {
	type Accent,
	ACCENT_LIGHT_MAX,
	ACCENT_LIGHT_MIN,
	type ButtonCursor,
	accentPresets,
	brandColor,
	findAccentPreset,
} from '#app/utils/accent.ts'
import { cn } from '#app/utils/misc.tsx'
import { type Theme } from '#app/utils/theme.server.ts'

/** The theme *preference* the segment can set — the resolved `Theme` plus `system`. */
type ThemeMode = Theme | 'system'

const themeModes: Array<{
	value: ThemeMode
	label: string
	icon: 'sun' | 'moon' | 'laptop'
}> = [
	{ value: 'light', label: 'Light', icon: 'sun' },
	{ value: 'dark', label: 'Dark', icon: 'moon' },
	{ value: 'system', label: 'System', icon: 'laptop' },
]

const cursorModes: Array<{ value: ButtonCursor; label: string }> = [
	{ value: 'default', label: 'Default' },
	{ value: 'pointer', label: 'Pointer' },
]

// The segment options are static (icon + label per mode), so build them once at
// module scope rather than re-allocating the Icon fragments on every re-render.
const themeSegmentOptions = themeModes.map((mode) => ({
	value: mode.value,
	label: (
		<>
			<Icon name={mode.icon} />
			{mode.label}
		</>
	),
}))

const cursorSegmentOptions = cursorModes.map((mode) => ({
	value: mode.value,
	label: mode.label,
}))

/**
 * Theme customizer — the navbar popover that lets a visitor re-theme the page
 * live (ADR 062). Collapsed it's a trigger pill showing the active accent;
 * expanded it's a card of controls: accent preset swatches, free Hue/Chroma/Light
 * sliders (Light clamped to the safe band), the 3-way theme segment, and the
 * button-cursor segment. It's mounted in the universal AppShell navbar on the
 * surfaces that expose the accent picker (marketing + full), subsuming the bare
 * accent swatches and theme toggle there.
 *
 * Every change rides the existing cookie + SSR plumbing — accent + cursor post
 * to `/resources/accent`, theme to `/resources/theme-switch` — so the whole page
 * re-tints live (optimistic) and persists across reload with no flash (EPT-10).
 * The card is a `Popover`, so its open/close motion rides the shared anchored
 * overlay contract (reduced-motion honoured); tokens only. The dock's own
 * controls always keep `cursor: pointer`, independent of the cursor pref.
 */
export function ThemeCustomizer({
	accent,
	cursor,
	theme,
}: {
	accent: Accent
	cursor: ButtonCursor
	theme: Theme | null
}) {
	const { t } = useTranslation()
	// Local accent/cursor track the controls so the dock's own UI reacts instantly
	// while the optimistic re-tint and cookie persistence catch up.
	const [draft, setDraft] = useState<Accent>(accent)
	const [draftCursor, setDraftCursor] = useState<ButtonCursor>(cursor)

	const accentFetcher = useFetcher()
	const themeFetcher = useFetcher()
	const activeTheme = useOptimisticThemeMode() ?? theme ?? 'system'

	const activePreset = findAccentPreset(draft)

	// The dock is a JS-only component (no `<noscript>` form), so it never sends
	// `redirectTo`. That field is the *no-JS* progressive-enhancement escape hatch
	// (see the `<ServerOnly>`-gated inputs on AccentSwitch/ThemeSwitch); sending it
	// from a fetcher makes the action return a single-fetch redirect to the index
	// `.data` URL, which 404s through the splat route. Omitting it lets the action
	// return `data()` — cookie still set, page still re-tints optimistically.

	/** Persist an accent change (preset or sliders) and re-tint optimistically. */
	function commitAccent(next: Accent, presetId?: string) {
		setDraft(next)
		void accentFetcher.submit(
			presetId ? { presetId } : { l: next.l, c: next.c, h: next.h },
			{ method: 'POST', action: '/resources/accent' },
		)
	}

	function commitCursor(next: ButtonCursor) {
		setDraftCursor(next)
		void accentFetcher.submit(
			{ cursor: next },
			{ method: 'POST', action: '/resources/accent' },
		)
	}

	function commitTheme(next: ThemeMode) {
		void themeFetcher.submit(
			{ theme: next },
			{ method: 'POST', action: '/resources/theme-switch' },
		)
	}

	const accentName = activePreset?.name ?? 'Custom'

	// Anchored in the universal AppShell navbar (ADR-068): an inline trigger pill
	// that toggles a Popover card hung below it (`side=bottom align=end`), rather
	// than a page-fixed dock. The Popover owns open state, `aria-expanded`,
	// outside-click / Esc dismissal, and the shared anchored overlay motion.
	return (
		<Popover>
			<PopoverTrigger asChild>
				<button
					type="button"
					aria-label="Customize theme"
					// The pill keeps its own `--card` surface while open (it opts out of
					// the trigger's default `--bg-active` pin).
					className="focus-cosy bg-card text-card-foreground border-border data-[state=open]:bg-card flex cursor-pointer items-center gap-2 rounded-full border py-1.5 pr-3 pl-1.5 outline-hidden"
				>
					<span
						className="size-5 rounded-full"
						style={{ backgroundColor: brandColor(draft) }}
					/>
					<span className="text-body-xs hidden font-medium sm:inline">
						{accentName}
					</span>
				</button>
			</PopoverTrigger>

			<PopoverContent align="end" role="region" aria-label="Theme customizer">
				<div className="flex items-center justify-between">
					<p className="text-body-xs font-semibold">{t('customizer.title')}</p>
					<PopoverClose asChild>
						<button
							type="button"
							aria-label="Close customizer"
							className="focus-cosy text-muted-foreground hover:text-foreground flex size-7 cursor-pointer items-center justify-center rounded-md outline-hidden hover:bg-[var(--bg-hover)]"
						>
							<Icon name="cross-1" size="sm" />
						</button>
					</PopoverClose>
				</div>

				{/* Accent preset swatches. */}
				<div className="mt-4 flex items-center gap-2">
					{accentPresets.map((preset) => {
						const active = preset.id === activePreset?.id
						return (
							<button
								key={preset.id}
								type="button"
								aria-pressed={active}
								title={preset.name}
								onClick={() => commitAccent(preset.accent, preset.id)}
								className={cn(
									'focus-cosy size-7 cursor-pointer rounded-full outline-hidden',
									// Selected ≠ focused: a persistent gap-then-ring in the
									// foreground colour (the cosy geometry, not the accent), so
									// the focus ring can still paint over it while tabbing.
									active &&
										'shadow-[0_0_0_2px_var(--ring-gap,var(--background)),0_0_0_4px_var(--foreground)]',
								)}
								style={{ backgroundColor: brandColor(preset.accent) }}
							>
								<span className="sr-only">{preset.name}</span>
							</button>
						)
					})}
				</div>

				{/* Free oklch sliders — gradient tracks built from the live accent. */}
				<div className="mt-5 grid gap-4">
					<AccentSlider
						label="Hue"
						value={draft.h}
						min={0}
						max={360}
						step={1}
						trackGradient={`linear-gradient(to right in oklch, oklch(${draft.l}% ${draft.c} 0), oklch(${draft.l}% ${draft.c} 120), oklch(${draft.l}% ${draft.c} 240), oklch(${draft.l}% ${draft.c} 360))`}
						onChange={(h) => commitAccent({ ...draft, h })}
					/>
					<AccentSlider
						label="Chroma"
						value={draft.c}
						min={0}
						max={0.3}
						step={0.005}
						trackGradient={`linear-gradient(to right in oklch, oklch(${draft.l}% 0 ${draft.h}), oklch(${draft.l}% 0.3 ${draft.h}))`}
						onChange={(c) => commitAccent({ ...draft, c })}
					/>
					<AccentSlider
						label="Light"
						value={draft.l}
						min={ACCENT_LIGHT_MIN}
						max={ACCENT_LIGHT_MAX}
						step={1}
						trackGradient={`linear-gradient(to right in oklch, oklch(${ACCENT_LIGHT_MIN}% ${draft.c} ${draft.h}), oklch(${ACCENT_LIGHT_MAX}% ${draft.c} ${draft.h}))`}
						onChange={(l) => commitAccent({ ...draft, l })}
					/>
				</div>

				{/* Theme segment — reuses the existing theme cookie + optimistic hook. */}
				<SegmentField label="Theme" className="mt-5">
					<Segmented
						aria-label="Theme"
						className="flex w-full"
						value={activeTheme}
						onValueChange={(value) => commitTheme(value as ThemeMode)}
						options={themeSegmentOptions}
					/>
				</SegmentField>

				{/* Button-cursor segment. */}
				<SegmentField label="Cursor" className="mt-3">
					<Segmented
						aria-label="Cursor"
						className="flex w-full"
						value={draftCursor}
						onValueChange={(value) => commitCursor(value as ButtonCursor)}
						options={cursorSegmentOptions}
					/>
				</SegmentField>
			</PopoverContent>
		</Popover>
	)
}

/** A labelled accent slider row built on the Foundation `Slider`. */
function AccentSlider({
	label,
	value,
	min,
	max,
	step,
	trackGradient,
	onChange,
}: {
	label: string
	value: number
	min: number
	max: number
	step: number
	trackGradient: string
	onChange: (value: number) => void
}) {
	return (
		<div className="grid gap-1.5">
			<span className="text-muted-foreground text-body-2xs font-medium">
				{label}
			</span>
			<Slider
				aria-label={label}
				value={value}
				min={min}
				max={max}
				step={step}
				trackGradient={trackGradient}
				onChange={onChange}
			/>
		</div>
	)
}

/** A labelled row: a muted caption above a `Segmented` control. */
function SegmentField({
	label,
	className,
	children,
}: {
	label: string
	className?: string
	children: React.ReactNode
}) {
	return (
		<div className={className}>
			<span className="text-muted-foreground text-body-2xs font-medium">
				{label}
			</span>
			<div className="mt-1.5">{children}</div>
		</div>
	)
}
